/**
 * StaggerContainer - Parent component for staggered child animations
 *
 * Orchestrates timing for child elements that animate in sequence.
 */

import { motion, type Variants } from 'framer-motion'
import { type ReactNode } from 'react'
import { STAGGER, SCROLL_REVEAL } from './motionVariants'
import { useReducedMotion } from './useReducedMotion'

interface StaggerContainerProps {
  children: ReactNode
  /** Delay between each child animation */
  stagger?: number
  /** Initial delay before first child */
  delayChildren?: number
  /** Use scroll-triggered animation */
  useScroll?: boolean
  /** Custom className */
  className?: string
  /** Custom styles */
  style?: React.CSSProperties
  /** Element to render as */
  as?: 'div' | 'section' | 'ul' | 'ol' | 'article'
}

export function StaggerContainer({
  children,
  stagger = STAGGER.normal,
  delayChildren = 0,
  useScroll = false,
  className,
  style,
  as = 'div',
}: StaggerContainerProps) {
  const prefersReducedMotion = useReducedMotion()

  // Reduced motion: render fully visible with no hidden state or scroll gating.
  if (prefersReducedMotion) {
    const Tag = as
    return <Tag className={className} style={style}>{children}</Tag>
  }

  const variants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: stagger,
        delayChildren,
      },
    },
  }

  const MotionComponent = motion[as]

  return (
    <MotionComponent
      initial="hidden"
      animate={useScroll ? undefined : 'visible'}
      whileInView={useScroll ? 'visible' : undefined}
      viewport={useScroll ? { once: SCROLL_REVEAL.once, margin: SCROLL_REVEAL.margin } : undefined}
      variants={variants}
      className={className}
      style={style}
    >
      {children}
    </MotionComponent>
  )
}

/**
 * StaggerItem - Child component that inherits stagger timing from parent
 *
 * Must be used inside a StaggerContainer for proper orchestration.
 */
interface StaggerItemProps {
  children: ReactNode
  className?: string
  style?: React.CSSProperties
  as?: 'div' | 'li' | 'article' | 'span'
}

export function StaggerItem({
  children,
  className,
  style,
  as = 'div',
}: StaggerItemProps) {
  const prefersReducedMotion = useReducedMotion()

  if (prefersReducedMotion) {
    const Tag = as
    return <Tag className={className} style={style}>{children}</Tag>
  }

  const variants: Variants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.2, 0.8, 0.2, 1],
      },
    },
  }

  const MotionComponent = motion[as]

  return (
    <MotionComponent
      variants={variants}
      className={className}
      style={style}
    >
      {children}
    </MotionComponent>
  )
}
